import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { listingsAPI, favoritesAPI, authAPI, uploadAPI, metaAPI, referralAPI, formatApiError } from '../lib/api';
import ListingCard from '../components/ListingCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Heart, Settings, Loader2, Trash2, Pencil, Save, X, Upload, ImagePlus, Crown, Share2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function Dashboard() {
  const { t } = useLang();
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({ name: '', phone: '', country: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [editListing, setEditListing] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [premium, setPremium] = useState({ is_premium: false, max_images: 3, max_description: 50 });
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);
  const defaultTab = searchParams.get('tab') || 'listings';

  const vehicleTypes = ['cars_suvs', 'trucks_buses', 'caravans_campers', 'motorcycles_bikes', 'industrial', 'car_transport', 'yachts_boats'];
  const fuelTypes = ['petrol', 'diesel', 'electric', 'hybrid', 'lpg', 'cng'];

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    setProfile({ name: user.name || '', phone: user.phone || '', country: user.country || '' });
    Promise.all([
      listingsAPI.getAll({ user_id: user._id }).then(r => setListings(r.data.listings)),
      favoritesAPI.getAll().then(r => setFavorites(r.data.favorites)).catch(() => {}),
      metaAPI.getCountries().then(r => setCountries(r.data.countries)).catch(() => {}),
      authAPI.premiumStatus().then(r => setPremium(r.data)).catch(() => {}),
      referralAPI.getMy().then(r => setReferral(r.data)).catch(() => {}),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, [user, navigate]);

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm(t('listing.deleteConfirm'))) return;
    try {
      await listingsAPI.delete(listingId);
      setListings(prev => prev.filter(l => l.id !== listingId));
      toast.success('Listing deleted');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleSaveProfile = async () => {
    if (!profile.name.trim() || !profile.phone.trim() || !profile.country.trim()) {
      toast.error('All profile fields are required');
      return;
    }
    setSavingProfile(true);
    try {
      await authAPI.updateProfile(profile);
      await checkAuth();
      toast.success('Profile updated');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingProfile(false); }
  };

  const handleRemoveFavorite = async (listingId) => {
    try {
      await favoritesAPI.remove(listingId);
      setFavorites(prev => prev.filter(l => l.id !== listingId));
      toast.success('Removed from favorites');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Edit listing
  const startEdit = (listing) => {
    setEditListing(listing);
    setEditForm({
      title: listing.title || '',
      description: listing.description || '',
      vehicle_type: listing.vehicle_type || 'cars_suvs',
      brand: listing.brand || '',
      model: listing.model || '',
      year: listing.year || 2024,
      price: listing.price || '',
      mileage: listing.mileage || '',
      fuel_type: listing.fuel_type || '',
      transmission: listing.transmission || '',
      power_hp: listing.power_hp || '',
      color: listing.color || '',
      country: listing.country || '',
      city: listing.city || '',
      images: listing.images || [],
    });
  };

  const cancelEdit = () => { setEditListing(null); setEditForm(null); };

  const handleEditUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const newImages = [...editForm.images];
    for (const file of files) {
      if (newImages.length >= premium.max_images) {
        toast.error(`Max ${premium.max_images} photos`);
        break;
      }
      try {
        const { data } = await uploadAPI.image(file);
        newImages.push(`${backendUrl}${data.url}`);
      } catch { toast.error('Upload failed'); }
    }
    setEditForm(p => ({ ...p, images: newImages }));
    setUploading(false);
    e.target.value = '';
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const payload = {
        title: editForm.title,
        description: editForm.description,
        price: parseFloat(editForm.price) || 0,
        mileage: editForm.mileage ? parseInt(editForm.mileage) : null,
        fuel_type: editForm.fuel_type || null,
        transmission: editForm.transmission || null,
        power_hp: editForm.power_hp ? parseInt(editForm.power_hp) : null,
        color: editForm.color || null,
        country: editForm.country || null,
        city: editForm.city || null,
        images: editForm.images,
      };
      await listingsAPI.update(editListing.id, payload);
      setListings(prev => prev.map(l => l.id === editListing.id ? { ...l, ...payload } : l));
      toast.success('Listing updated');
      cancelEdit();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingEdit(false); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen bg-[#0B0F19]"><Loader2 className="w-8 h-8 animate-spin text-[#FADB5F]" /></div>;
  const inputCls = "bg-[#0B0F19] border-[#1E293B] text-[#F1F5F9] placeholder:text-[#475569]";
  const isCarTransport = editForm?.vehicle_type === 'car_transport';

  return (
    <div className="min-h-screen bg-[#0B0F19]" data-testid="dashboard-page">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-[#F1F5F9] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('dashboard.title')}
            </h1>
            <p className="text-sm text-[#94A3B8] mt-1">{user?.name} &middot; {user?.email}</p>
          </div>
          <Link to="/create-listing">
            <Button className="bg-[#FADB5F] text-[#0F172A] hover:bg-[#E5C34A] font-semibold" data-testid="dashboard-create-btn">
              {t('nav.create')}
            </Button>
          </Link>
        </div>

        <Tabs defaultValue={defaultTab} className="space-y-6">
          <TabsList className="bg-[#141B2D] border border-[#1E293B] flex-wrap h-auto gap-1 p-1" data-testid="dashboard-tabs">
            <TabsTrigger value="listings" className="gap-2 data-[state=active]:bg-[#0A3A82] data-[state=active]:text-white text-[#94A3B8] text-xs" data-testid="tab-listings">
              <FileText className="w-3.5 h-3.5" /> {t('dashboard.myListings')} ({listings.length})
            </TabsTrigger>
            <TabsTrigger value="favorites" className="gap-2 data-[state=active]:bg-[#0A3A82] data-[state=active]:text-white text-[#94A3B8] text-xs" data-testid="tab-favorites">
              <Heart className="w-3.5 h-3.5" /> Favorites ({favorites.length})
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2 data-[state=active]:bg-[#0A3A82] data-[state=active]:text-white text-[#94A3B8] text-xs" data-testid="tab-settings">
              <Settings className="w-3.5 h-3.5" /> Settings
            </TabsTrigger>
            <TabsTrigger value="referral" className="gap-2 data-[state=active]:bg-[#0A3A82] data-[state=active]:text-white text-[#94A3B8] text-xs" data-testid="tab-referral">
              <Share2 className="w-3.5 h-3.5" /> Invite Friends
            </TabsTrigger>
          </TabsList>

          {/* My Listings */}
          <TabsContent value="listings">
            {/* Edit Listing Modal */}
            {editForm && (
              <div className="bg-[#141B2D] rounded-xl border border-[#FADB5F]/30 p-6 mb-6" data-testid="edit-listing-form">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-[#F1F5F9]" style={{ fontFamily: 'Outfit, sans-serif' }}>Edit: {editListing.title}</h3>
                  <button onClick={cancelEdit} className="text-[#94A3B8] hover:text-[#F1F5F9]"><X className="w-5 h-5" /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1 block">Title *</label>
                    <Input value={editForm.title} onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))} className={inputCls} />
                  </div>
                  {!isCarTransport && (
                    <>
                      <div>
                        <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1 block">Price (EUR) *</label>
                        <Input type="number" value={editForm.price} onChange={e => setEditForm(p => ({ ...p, price: e.target.value }))} className={inputCls} />
                      </div>
                      <div>
                        <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1 block">{t('listing.mileage')}</label>
                        <Input type="number" value={editForm.mileage} onChange={e => setEditForm(p => ({ ...p, mileage: e.target.value }))} className={inputCls} />
                      </div>
                      <div>
                        <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1 block">{t('listing.fuel')}</label>
                        <Select value={editForm.fuel_type || ''} onValueChange={v => setEditForm(p => ({ ...p, fuel_type: v }))}>
                          <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                          <SelectContent>{fuelTypes.map(f => <SelectItem key={f} value={f}>{t(`fuel_types.${f}`)}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1 block">{t('listing.transmission')}</label>
                        <Select value={editForm.transmission || ''} onValueChange={v => setEditForm(p => ({ ...p, transmission: v }))}>
                          <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="manual">{t('transmission_types.manual')}</SelectItem>
                            <SelectItem value="automatic">{t('transmission_types.automatic')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1 block">{t('listing.power')} (HP)</label>
                        <Input type="number" value={editForm.power_hp} onChange={e => setEditForm(p => ({ ...p, power_hp: e.target.value }))} className={inputCls} />
                      </div>
                      <div>
                        <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1 block">{t('listing.color')}</label>
                        <Input value={editForm.color} onChange={e => setEditForm(p => ({ ...p, color: e.target.value }))} className={inputCls} />
                      </div>
                    </>
                  )}
                  <div>
                    <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1 block">{t('filters.country')}</label>
                    <Select value={editForm.country || ''} onValueChange={v => setEditForm(p => ({ ...p, country: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1 block">City</label>
                    <Input value={editForm.city} onChange={e => setEditForm(p => ({ ...p, city: e.target.value }))} className={inputCls} />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1 block">{t('listing.description')} *</label>
                  <Textarea value={editForm.description} onChange={e => {
                    if (e.target.value.length <= premium.max_description) setEditForm(p => ({ ...p, description: e.target.value }));
                  }} rows={3} maxLength={premium.max_description} className={inputCls} />
                  <span className="text-[10px] text-[#475569]">{editForm.description.length}/{premium.max_description}</span>
                </div>
                {/* Photos */}
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8]">Photos</label>
                    <span className="text-[10px] text-[#94A3B8]">{editForm.images.length}/{premium.max_images}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {editForm.images.map((img, i) => (
                      <div key={i} className="relative w-20 h-14 rounded-md overflow-hidden border border-[#1E293B] group">
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => setEditForm(p => ({ ...p, images: p.images.filter((_, idx) => idx !== i) }))} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition text-[10px]"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                    {editForm.images.length < premium.max_images && (
                      <label className="w-20 h-14 rounded-md border-2 border-dashed border-[#1E293B] hover:border-[#FADB5F]/50 flex items-center justify-center cursor-pointer transition-colors">
                        <input type="file" accept="image/*" multiple onChange={handleEditUpload} className="hidden" />
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin text-[#FADB5F]" /> : <Upload className="w-4 h-4 text-[#475569]" />}
                      </label>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={saveEdit} disabled={savingEdit} className="flex-1 bg-[#FADB5F] text-[#0F172A] hover:bg-[#E5C34A] font-semibold">
                    {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save</>}
                  </Button>
                  <Button variant="outline" onClick={cancelEdit} className="border-[#1E293B] text-[#94A3B8]">Cancel</Button>
                </div>
              </div>
            )}

            {listings.length === 0 ? (
              <div className="text-center py-16 bg-[#141B2D] rounded-xl border border-[#1E293B]" data-testid="no-listings">
                <FileText className="w-12 h-12 text-[#475569] mx-auto mb-4" />
                <p className="text-sm text-[#94A3B8]">{t('dashboard.noListings')}</p>
                <Link to="/create-listing"><Button className="mt-4 bg-[#0A3A82] text-white hover:bg-[#082F6B]">{t('nav.create')}</Button></Link>
              </div>
            ) : (
              <div className="space-y-4" data-testid="dashboard-listings-grid">
                {listings.map(listing => (
                  <div key={listing.id} className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-4 flex gap-4 items-start" data-testid={`my-listing-${listing.id}`}>
                    <Link to={`/listings/${listing.id}`} className="shrink-0">
                      <img src={listing.images?.[0] || ''} alt="" className="w-32 h-20 object-cover rounded-lg bg-[#1E293B]" onError={e => { e.target.style.display='none'; }} />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link to={`/listings/${listing.id}`} className="hover:underline">
                        <h3 className="text-sm font-semibold text-[#F1F5F9] truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{listing.title}</h3>
                      </Link>
                      <p className="text-base font-bold text-[#FADB5F] mt-0.5">{listing.price?.toLocaleString()} {listing.currency || 'EUR'}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-[#94A3B8]">
                        <span>{listing.brand} {listing.model}</span>
                        <span>&middot;</span>
                        <span>{listing.year}</span>
                        {listing.is_featured && <Badge className="bg-[#FADB5F]/20 text-[#FADB5F] text-[10px] px-1.5 py-0 border-0">FEATURED</Badge>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => startEdit(listing)} className="text-[#94A3B8] hover:text-[#FADB5F]" data-testid={`edit-${listing.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteListing(listing.id)} className="text-[#94A3B8] hover:text-red-400" data-testid={`delete-${listing.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Favorites */}
          <TabsContent value="favorites">
            {favorites.length === 0 ? (
              <div className="text-center py-16 bg-[#141B2D] rounded-xl border border-[#1E293B]" data-testid="no-favorites">
                <Heart className="w-12 h-12 text-[#475569] mx-auto mb-4" />
                <p className="text-sm text-[#94A3B8]">No favorite listings yet</p>
                <Link to="/listings"><Button className="mt-4 bg-[#0A3A82] text-white hover:bg-[#082F6B]">{t('nav.listings')}</Button></Link>
              </div>
            ) : (
              <div className="space-y-4" data-testid="favorites-list">
                {favorites.map(listing => (
                  <div key={listing.id} className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-4 flex gap-4 items-start" data-testid={`fav-${listing.id}`}>
                    <Link to={`/listings/${listing.id}`} className="shrink-0">
                      <img src={listing.images?.[0] || ''} alt="" className="w-32 h-20 object-cover rounded-lg bg-[#1E293B]" onError={e => { e.target.style.display='none'; }} />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link to={`/listings/${listing.id}`} className="hover:underline">
                        <h3 className="text-sm font-semibold text-[#F1F5F9] truncate">{listing.title}</h3>
                      </Link>
                      <p className="text-base font-bold text-[#FADB5F] mt-0.5">{listing.price?.toLocaleString()} EUR</p>
                      <span className="text-xs text-[#94A3B8]">{listing.city}, {listing.country}</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveFavorite(listing.id)} className="text-red-400 hover:text-red-300 shrink-0" data-testid={`unfav-${listing.id}`}>
                      <Heart className="w-4 h-4 fill-red-400" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Settings */}
          <TabsContent value="settings">
            <div className="max-w-lg space-y-6">
              <div className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6" data-testid="profile-settings">
                <h3 className="text-base font-semibold text-[#F1F5F9] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Settings className="w-4 h-4 inline mr-2" />Profile Settings
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('auth.name')} *</label>
                    <Input value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} required className={inputCls} data-testid="profile-name" />
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('auth.email')} *</label>
                    <Input value={user?.email || ''} disabled className="bg-[#0B0F19] border-[#1E293B] text-[#475569]" />
                    <p className="text-[10px] text-[#475569] mt-1">Email cannot be changed</p>
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('auth.phone')} *</label>
                    <Input value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} required className={inputCls} data-testid="profile-phone" />
                  </div>
                  <div>
                    <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('auth.country')} *</label>
                    <Input value={profile.country} onChange={e => setProfile(p => ({ ...p, country: e.target.value }))} required className={inputCls} data-testid="profile-country" />
                  </div>
                  <Button onClick={handleSaveProfile} disabled={savingProfile} className="w-full bg-[#FADB5F] text-[#0F172A] hover:bg-[#E5C34A] font-semibold" data-testid="save-profile-btn">
                    {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
                  </Button>
                </div>
              </div>
              <div className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6">
                <h3 className="text-sm font-semibold text-[#F1F5F9] mb-2">Account Info</h3>
                <div className="text-xs text-[#94A3B8] space-y-1">
                  <p>Role: <span className="text-[#F1F5F9]">{user?.role || 'user'}</span></p>
                  <p>Member since: <span className="text-[#F1F5F9]">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</span></p>
                  <p>Listings: <span className="text-[#F1F5F9]">{listings.length}</span></p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Referral / Invite Friends */}
          <TabsContent value="referral">
            <div className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6" data-testid="referral-section">
              <div className="max-w-lg mx-auto text-center">
                <div className="w-16 h-16 rounded-2xl bg-[#FADB5F]/10 flex items-center justify-center mx-auto mb-4">
                  <Share2 className="w-8 h-8 text-[#FADB5F]" />
                </div>
                <h3 className="text-xl font-bold text-[#F1F5F9] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Invite Friends to EuroMoto
                </h3>
                <p className="text-sm text-[#94A3B8] mb-6">
                  Share your referral link and grow the EuroMoto community across Europe!
                </p>

                {referral && (
                  <>
                    <div className="bg-[#0B0F19] rounded-xl border border-[#1E293B] p-4 mb-4">
                      <p className="text-xs text-[#64748B] mb-2">Your referral link:</p>
                      <div className="flex items-center gap-2">
                        <input
                          readOnly
                          value={referral.referral_link}
                          className="flex-1 bg-[#141B2D] border border-[#1E293B] rounded-lg px-3 py-2 text-sm text-[#F1F5F9] font-mono"
                          data-testid="referral-link-input"
                        />
                        <Button
                          onClick={() => {
                            navigator.clipboard.writeText(referral.referral_link);
                            setCopied(true);
                            toast.success('Link copied!');
                            setTimeout(() => setCopied(false), 2000);
                          }}
                          size="sm"
                          className="bg-[#FADB5F] text-[#0F172A] hover:bg-[#E5C34A] shrink-0"
                          data-testid="copy-referral-btn"
                        >
                          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-6">
                      <div className="bg-[#0B0F19] rounded-xl border border-[#1E293B] p-4">
                        <p className="text-2xl font-bold text-[#FADB5F]" style={{ fontFamily: 'Outfit, sans-serif' }}>{referral.referral_count}</p>
                        <p className="text-xs text-[#64748B]">Friends joined</p>
                      </div>
                      <div className="bg-[#0B0F19] rounded-xl border border-[#1E293B] p-4">
                        <p className="text-2xl font-bold text-[#FADB5F] font-mono" style={{ fontFamily: 'Outfit, sans-serif' }}>{referral.ref_code}</p>
                        <p className="text-xs text-[#64748B]">Your code</p>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        onClick={() => {
                          const text = `Check out EuroMoto.Online — buy & sell vehicles across Europe! Join with my link: ${referral.referral_link}`;
                          if (navigator.share) {
                            navigator.share({ title: 'EuroMoto.Online', text, url: referral.referral_link });
                          } else {
                            navigator.clipboard.writeText(text);
                            toast.success('Share text copied!');
                          }
                        }}
                        className="flex-1 bg-[#0A3A82] text-white hover:bg-[#0A3A82]/80"
                        data-testid="share-referral-btn"
                      >
                        <Share2 className="w-4 h-4 mr-2" /> Share
                      </Button>
                      <a
                        href={`https://wa.me/?text=${encodeURIComponent(`Check out EuroMoto.Online! Join with my link: ${referral.referral_link}`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-[#25D366] text-white font-medium text-sm hover:bg-[#25D366]/80"
                        data-testid="whatsapp-referral-btn"
                      >
                        WhatsApp
                      </a>
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referral.referral_link)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md bg-[#1877F2] text-white font-medium text-sm hover:bg-[#1877F2]/80"
                        data-testid="facebook-referral-btn"
                      >
                        Facebook
                      </a>
                    </div>
                  </>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
